import * as React from "react";
import {FC, useContext, useEffect, useCallback} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Skeleton,
	Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {INotificationSeverity, APIState, NotificationContext} from "@buildwitt/shapeshift-identity";
import {AnnouncementPreview} from "@/pages/HomePage/Components/AnnouncementPreview";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import {useAnnouncement} from "@/hooks/useAnnouncement";
import {LocaleContext} from "@/contexts/localeContext";
import {IUserEntity} from "@buildwitt/shapeshift-types/entities";
import {useSetAnnouncementCompletion} from "@/hooks/useSetAnnouncementCompletion";
import {IModuleEnrollment} from "@buildwitt/shapeshift-types";
import {AnnouncementContext} from "@/contexts/announcementContext";

const useStyles = makeStyles()((theme) => ({
	dialogPaper: {
		backgroundColor: theme.palette.background.paperElevation2,
		borderColor: theme.palette.background.paperElevation2,
		borderRadius: 16,
		marginTop: 61,
		color: "black"
	},
	dialogContentRoot: {
		padding: "5px",
		textAlign: "left"
	},
	dialogActionsRoot: {
		justifyContent: "flex-start"
	},
	dialogTitle: {
		padding: 0,
		paddingBottom: "10px",
		fontSize: "24px",
		textAlign: "left"
	},
	closeButton: {
		position: "absolute",
		right: 8,
		top: 8
	},
	skeletonTitle: {
		marginTop: "8px",
		marginBottom: "8px",
		fontSize: "34px"
	},
	skeletonLine: {
		marginTop: "8px",
		marginBottom: "8px",
		fontSize: "16px",
		color: "rgba(249, 249, 249, 0.7)",
		fontWeight: 400
	}
}));

interface IProps {
	open: boolean;
	onClose: () => void;
	announcementSlug: string;
	author: IUserEntity;
}

const ANNOUNCEMENT_POPULATE = ["files", "links", "enrollment.videoTime"];

export const AnnouncementModal: FC<IProps> = ({open, onClose, announcementSlug, author}) => {
	const {classes} = useStyles();
	const {t} = useSSTranslation();
	const {notify} = useContext(NotificationContext);
	const mq = useMq();
	const [{locale}] = useContext(LocaleContext);
	const dataHook = useAnnouncement(announcementSlug, undefined, ANNOUNCEMENT_POPULATE, locale)
	const {announcement, getAnnouncement} = dataHook;
	const {setComplete, apiResult: completeResponse} = useSetAnnouncementCompletion();
	const {announcementDispatch} = useContext(AnnouncementContext);

	useEffect(() => {
		if(announcementSlug && !announcement){
			getAnnouncement(announcementSlug, ANNOUNCEMENT_POPULATE, locale);
		}
	}, [announcementSlug, locale])

	const onCloseModal = () => {
		if (!announcement || announcement.complete) {
			onClose();
		}
		else
		{
			setComplete(announcementSlug);
		}
	}

	useEffect(() => {
		handleCompletion(completeResponse)
	}, [completeResponse])

	const handleCompletion = useCallback(
		(e: APIState<IModuleEnrollment>) => {
			if (e.isError) {
				notify(
					"Please try again",
					"Something went wrong",
					INotificationSeverity.error,
					5000
				);
				onClose();
				return;
			}

			if (!e.isLoading && e.statusCode === 200 && e.data?.id) {
				announcementDispatch({
					type: "setAnnouncementComplete",
					id: announcement.id
				});

				if (e.data.complete) {
					notify(
						null,
						"Announcement completed",
						INotificationSeverity.success,
						5000
					);
				}
				onClose();
			}
		},
		[announcementDispatch, announcement?.id, notify]
	);

	return (
		<Dialog
			open={open}
			fullScreen={mq.mobile}
			onClose={onCloseModal}
			aria-label="Announcement dialog"
			aria-modal="true"
			data-test-id="announcement-modal"
			PaperProps={{
				className: classes.dialogPaper,
				style: {
					minHeight: "700px",
					paddingBottom: mq.mobile ? "40px" : "10px"
				}
			}}
		>
			<DialogTitle
				id="announcement-dialog-title"
				className={classes.dialogTitle}
				data-test-id="announcement-modal-title"
			>
				{t("common_announcement", "Announcement")}
			</DialogTitle>

			<IconButton
				onClick={onCloseModal}
				disabled={completeResponse.isLoading}
				className={classes.closeButton}
				aria-label="Close announcement dialog"
				data-test-id="announcement-modal-close-btn"
			>
				<CloseIcon />
			</IconButton>

			{!announcement ? (
				<>
					<DialogContent
						className={classes.dialogContentRoot}
						aria-label="Announcement content"
						data-test-id="announcement-modal-content"
					>
						<Skeleton
							variant="text"
							height={48}
							width="80%"
							className={classes.skeletonTitle}
							animation="wave"
						/>
						<Skeleton
							variant="text"
							height={24}
							width="100%"
							className={classes.skeletonLine}
							animation="wave"
						/>
						<Skeleton
							variant="text"
							height={24}
							width="90%"
							className={classes.skeletonLine}
							animation="wave"
						/>
					</DialogContent>

					<DialogActions
						className={classes.dialogActionsRoot}
						aria-label="Announcement author information"
						data-test-id="announcement-modal-footer"
					>
						<Stack direction="row" spacing={1} alignItems="center">
							<Skeleton variant="circular" width={40} height={40} animation="wave" />
							<Stack direction="column" spacing={0.5} alignItems="flex-start">
								<Skeleton variant="text" width={120} height={20} animation="wave" />
								<Skeleton variant="text" width={100} height={16} animation="wave" />
								<Skeleton variant="text" width={80} height={16} animation="wave" />
							</Stack>
						</Stack>
					</DialogActions>
				</>
			) : (
				<AnnouncementPreview announcement={announcement} author={author} />
			)}
		</Dialog>
	);
};
